<template>
  <ASiteContainer class="pt-24 pb-28 sm:max-w-3xl sm:px-inherit px-8">
    <slot name="header">
      <AMinimalHeader
        class="bg-gradient-to-r from-primary to-cyan-400 border-0"
      />
    </slot>
    <main class="rounded-lg">
      <slot />
    </main>
    <div
      class="w-full bg-gray-50 fixed bottom-0 sm:fixed sm:top-16 right-0 sm:bottom-auto overflow-hidden sm:left-0 sm:w-72 sm:h-full z-10"
    >
      <div v-if="!$device.isMobile" class="pl-8 pt-6 pb-3">
        <div class="flex items-center justify-start">
          <UAvatar
            size="lg"
            class="!size-10 sm:ring-1 bg-white sm:ring-slate-300"
            :alt="userFirstName"
          />
          <div class="flex ml-3 flex-col">
            <p class="font-bold text-[0.95rem]">
              {{ $t("layouts.account.welcome", { firstname: userFirstName }) }}
            </p>
          </div>
        </div>
      </div>
      <div
        class="flex sm:flex-col justify-between sm:justify-start max-w-site sm:max-w-none mx-auto items-center sm:items-start divide-x sm:divide-x-0 sm:divide-y"
      >
        <ULink
          :to="localePath({ name: 'account' })"
          active-class="!text-brand"
          class="flex-1 flex sm:flex-row sm:w-full flex-col items-center justify-center sm:justify-start py-2.5 sm:py-4 sm:pl-8"
        >
          <UIcon class="text-2xl" name="mdi:palm-tree" />
          <p class="text-sm font-bold sm:ml-2">
            {{ $t("layouts.account.trips") }}
          </p>
        </ULink>
        <ULink
          :to="localePath({ name: 'account-infos' })"
          active-class="!text-brand"
          class="flex-1 flex sm:flex-row sm:w-full flex-col items-center justify-center sm:justify-start py-2.5 sm:py-4 sm:pl-8"
        >
          <UIcon class="text-2xl" name="mdi:account-cog" />
          <p class="text-sm font-bold sm:ml-2">
            {{ $t("layouts.account.account") }}
          </p>
        </ULink>
        <ULink
          :to="localePath({ name: 'account-help' })"
          active-class="!text-brand"
          class="flex-1 flex sm:flex-row sm:w-full flex-col items-center justify-center sm:justify-start py-2.5 sm:py-4 sm:pl-8"
        >
          <UIcon class="text-2xl" name="mdi:help-circle-outline" />
          <p class="text-sm font-bold sm:ml-2">
            {{ $t("layouts.account.help") }}
          </p>
        </ULink>
      </div>
      <ULink
        v-if="!$device.isMobile"
        active-class="!text-brand"
        class="flex-1 flex flex-row items-center justify-start py-4 mb-4 text-red-500 sm:pl-8 fixed bottom-0 group"
        @click="signOut({ callbackUrl: localePath({ name: 'login' }) })"
      >
        <UIcon
          class="text-2xl group-hover:ml-1 group-hover:-mr-1 transition-all duration-300"
          name="mdi:logout"
        />
        <p class="text-sm font-bold sm:ml-2">
          {{ $t("layouts.account.logout") }}
        </p>
      </ULink>
    </div>

    <AModal
      modal-id="details-modal"
      :title="$t('layouts.account.completeProfile')"
      :click-to-close="false"
      :can-be-closed="false"
      :esc-to-close="false"
    >
      <UAlert
        color="brand"
        variant="subtle"
        class="mb-4"
        icon="mdi:warning-outline"
      >
        <template #description>
          {{ $t("layouts.account.completeProfileMessage") }}
        </template>
      </UAlert>

      <AForm
        :state="profileState"
        :submit-attrs="{ block: true, size: 'lg' }"
        class="!max-w-full"
        show-validation-toast
        :handler="
          ({ data }: any) =>
            $fetch('/api/users/current/update_missing_details', {
              method: 'PUT',
              body: data,
            })
        "
        @success="detailsUpdated"
      >
        <div class="grid sm:grid-cols-2 gap-x-4">
          <UFormGroup
            :label="$t('pages.account.infos.lastname')"
            required
            name="lastname"
          >
            <UInput
              v-model="profileState.lastname"
              size="lg"
              placeholder="Durand"
            />
          </UFormGroup>
          <UFormGroup
            :label="$t('pages.account.infos.firstname')"
            required
            name="firstname"
          >
            <UInput
              v-model="profileState.firstname"
              size="lg"
              placeholder="Martin"
            />
          </UFormGroup>
        </div>
        <UFormGroup
          :label="$t('pages.account.infos.phone')"
          required
          name="phone"
        >
          <AInputPhone
            v-model="profileState.phone"
            size="lg"
            :placeholder="
              $t('components.resources.users.userForm.phone.placeholder')
            "
            :default-country="$me?.countryIso"
          />
        </UFormGroup>
      </AForm>
    </AModal>
  </ASiteContainer>
</template>

<script lang="ts" setup>
const localePath = useLocalePath()

const { refresh, signOut } = useAuth()
const { $me, $useVfm } = useNuxtApp()
const modal = $useVfm()
const userFirstName = computed(() => $me?.value?.firstname || '')

// Create profile state for the modal form
const profileState = ref({
  firstname: $me.value?.firstname,
  lastname: $me.value?.lastname,
  phone: $me.value?.phone,
})

// Check if user profile is incomplete
const isProfileIncomplete = computed(() => {
  return !$me.value?.firstname || !$me.value?.lastname || !$me.value?.phone
})

// Function to close the profile modal
function detailsUpdated() {
  modal.close('details-modal')
  refresh()
}

// Open modal on mount if profile is incomplete
onMounted(() => {
  if (isProfileIncomplete.value) {
    modal.open('details-modal')
  }
})

useRobotsRule(false)
</script>
